<script setup lang="ts">
import UserBadge from '@/components/Profile/UserBadge.vue';
import type { DayOffGroup } from '@/composables/useDayOff';
import type { Employee } from '@/types/api';
import { formatDisplayDate } from '@/utils/time';
import Button from 'primevue/button';
import Card from 'primevue/card';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  group: DayOffGroup;
  user?: Employee; // For manager views where we need to show the user
  showUser?: boolean;
  showDeleteButton?: boolean;
  showStatus?: boolean;
  variant?: 'default' | 'pending' | 'approved';
}>();

const emit = defineEmits<{
  (e: 'delete', group: DayOffGroup): void;
}>();

const cardClasses = computed(() => {
  const baseClasses = 'rounded-md border my-2 w-full';
  switch (props.variant) {
    case 'pending':
      return `${baseClasses} border-orange-200 bg-orange-50`;
    case 'approved':
      return `${baseClasses} border-green-200 bg-green-50`;
    default:
      return `${baseClasses} border-primary-50`;
  }
});

const statusClasses = computed(() => {
  switch (props.group.status.value) {
    case 'pending':
      return 'bg-orange-100 text-orange-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'declined':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
});
</script>

<template>
  <Card :class="cardClasses" :pt="{ content: { class: 'p-2' } }">
    <template #content>
      <div class="flex flex-col gap-2">
        <!-- User badge (for manager view) -->
        <UserBadge v-if="showUser && user" :user="user" :show-name="true" :color="user.kitchen_position?.color" />

        <!-- Status and group info -->
        <div v-if="showStatus" class="flex w-full flex-row items-center justify-between">
          <span :class="['rounded-full px-2 py-1 text-xs font-medium', statusClasses]">
            {{ t(`dayOff.status.${group.status.value}`) }}
          </span>
          <div class="flex flex-row items-center gap-2 text-xs text-gray-600">
            <span v-if="group.isGroup" class="rounded-full bg-blue-100 px-2 py-1 text-blue-800">
              {{ t('dayOff.groupedDays', { count: group.daysOff.length }) }}
            </span>
          </div>
        </div>

        <!-- Date range and action button -->
        <div class="flex w-full flex-row items-center justify-between gap-2">
          <div class="flex flex-row items-center gap-2 font-bold">
            <span>{{ formatDisplayDate(group.startDate) }}</span>
            <span v-if="group.isGroup" class="mx-1"> - </span>
            <span v-if="group.isGroup">{{ formatDisplayDate(group.endDate) }}</span>
            <span v-if="!showUser" class="ml-2 text-xs text-gray-500">
              ({{ group.daysOff.length }} {{ group.daysOff.length === 1 ? t('dayOff.day') : t('dayOff.days') }})
            </span>
          </div>

          <Button v-if="showDeleteButton" icon="pi pi-trash" severity="danger" text :aria-label="t('common.delete')" @click="emit('delete', group)" />
        </div>

        <!-- Additional info -->
        <p v-if="group.info" class="mt-1 text-xs text-gray-500">{{ group.info }}</p>
      </div>
    </template>
  </Card>
</template>
